/**
 * Single source of truth for all supported languages in Behtari platform.
 * Import this in all language switcher components to ensure consistency.
 */

// Increment this version when making language system changes to bust cache
export const LANGUAGES_VERSION = "3.0.0";

export interface LanguageConfig {
  code: string;
  name: string;        // English name
  nativeName: string;  // Native script name
  flag: string;        // Emoji flag
  isRTL: boolean;      // Right-to-left script
  fontClass: string;   // CSS class for correct typography
}

export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧", isRTL: false, fontClass: "" },
  { code: "ur", name: "Urdu", nativeName: "اردو", flag: "🇵🇰", isRTL: true, fontClass: "font-urdu" },
  { code: "sd", name: "Sindhi", nativeName: "سنڌي", flag: "🇵🇰", isRTL: true, fontClass: "font-sindhi" },
  { code: "skr", name: "Saraiki", nativeName: "سرائیکی", flag: "🇵🇰", isRTL: true, fontClass: "font-saraiki" },
  { code: "bal", name: "Balochi", nativeName: "بلوچی", flag: "🇵🇰", isRTL: true, fontClass: "font-balochi" },
  { code: "ps", name: "Pashto", nativeName: "پښتو", flag: "🇦🇫", isRTL: true, fontClass: "font-pashto" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦", isRTL: true, fontClass: "font-arabic" },
];

export const RTL_LANGUAGES = SUPPORTED_LANGUAGES.filter(l => l.isRTL).map(l => l.code);

export const FONT_CLASS_MAP: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.filter(l => l.fontClass).map(l => [l.code, l.fontClass])
);

/**
 * Get the appropriate font class for a given language code.
 * Returns empty string for LTR languages (uses default system font).
 */
export const getFontClass = (languageCode: string): string => {
  return FONT_CLASS_MAP[languageCode] || "";
};

/**
 * Check if a language code represents an RTL language.
 */
export const isRTLLanguage = (languageCode: string): boolean => {
  return RTL_LANGUAGES.includes(languageCode);
};

/**
 * Get language config by code.
 */
export const getLanguageByCode = (code: string): LanguageConfig | undefined => {
  return SUPPORTED_LANGUAGES.find(l => l.code === code);
};

export default SUPPORTED_LANGUAGES;
